import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { BrandLogo } from "@/components/BrandLogo";
import { peso } from "@/lib/api";
import { ShoppingCart, Minus, Plus, Trash2, User, ClipboardList, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

export function CustomerLayout({ children }) {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { count, items, subtotal, setQty, remove, open, setOpen } = useCart();

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F5]">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-[#1B5E20]/10">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <button onClick={() => nav("/")} data-testid="home-logo-btn">
            <BrandLogo size="md" showTagline />
          </button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="hidden sm:flex" data-testid="nav-orders-btn"
              onClick={() => nav(user ? "/my-orders" : "/login")}>
              <ClipboardList size={18} /> <span className="ml-1">My Orders</span>
            </Button>
            <Button variant="ghost" size="sm" className="hidden sm:flex" data-testid="nav-track-btn" onClick={() => nav("/track")}>
              <MapPin size={18} /> <span className="ml-1">Track</span>
            </Button>
            {user ? (
              <Button variant="ghost" size="sm" data-testid="nav-account-btn" onClick={() => nav("/account")}>
                <User size={18} /> <span className="ml-1 hidden sm:inline">{user.name?.split(" ")[0]}</span>
              </Button>
            ) : (
              <Button variant="ghost" size="sm" data-testid="nav-login-btn" onClick={() => nav("/login")}>
                <User size={18} /> <span className="ml-1">Login</span>
              </Button>
            )}
            <button onClick={() => setOpen(true)} data-testid="cart-open-btn"
              className="relative rounded-full bg-[#1B5E20] text-white h-10 w-10 grid place-items-center hover:bg-[#144718] transition-colors">
              <ShoppingCart size={18} />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-[#C62828] text-white text-[10px] font-bold h-5 min-w-5 px-1 rounded-full grid place-items-center">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-[#0E3A14] text-white mt-16">
        <div className="max-w-6xl mx-auto px-4 py-10 grid gap-6 md:grid-cols-3">
          <div>
            <BrandLogo size="md" light showTagline />
            <p className="text-white/60 text-sm mt-4">Created by Michael S. Carlos</p>
            <p className="text-white/60 text-sm">AngelSabaw.mistermicpos.co</p>
          </div>
          <div className="text-sm">
            <div className="font-semibold text-[#FFB300] mb-2">Company</div>
            <ul className="space-y-1 text-white/70">
              <li>Privacy</li><li>Terms</li><li>Contact</li><li>Help</li>
            </ul>
          </div>
          <div className="text-sm">
            <div className="font-semibold text-[#FFB300] mb-2">Service Area</div>
            <p className="text-white/70">Canyon Ranch Community<br/>Brgy. Lantic, Carmona, Cavite</p>
            <button onClick={() => nav("/store")} className="text-white/40 text-xs mt-4 hover:text-white/70" data-testid="footer-staff-btn">Staff Portal →</button>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-white/40">© 2026 MisterMic's AngelSabaw. GOOD SOUP. GOOD DAY.</div>
      </footer>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
          <SheetHeader><SheetTitle className="font-display text-xl">Your Cart</SheetTitle></SheetHeader>
          <div className="flex-1 overflow-y-auto py-4 space-y-3">
            {items.length === 0 && <p className="text-muted-foreground text-center py-10">Your cart is empty.</p>}
            {items.map((i) => (
              <div key={i.product.id} className="flex gap-3 items-center bg-white rounded-xl p-3 border" data-testid={`cart-item-${i.product.id}`}>
                {i.product.image_url && <img src={i.product.image_url} alt="" className="h-14 w-14 rounded-lg object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{i.product.name}</div>
                  <div className="text-[#1B5E20] font-bold text-sm">{peso(i.product.price)}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setQty(i.product.id, i.quantity - 1)} className="h-7 w-7 grid place-items-center rounded-md border" data-testid={`cart-dec-${i.product.id}`}><Minus size={14} /></button>
                  <span className="w-6 text-center font-semibold text-sm">{i.quantity}</span>
                  <button onClick={() => setQty(i.product.id, i.quantity + 1)} className="h-7 w-7 grid place-items-center rounded-md border" data-testid={`cart-inc-${i.product.id}`}><Plus size={14} /></button>
                  <button onClick={() => remove(i.product.id)} className="h-7 w-7 grid place-items-center rounded-md text-[#C62828]" data-testid={`cart-remove-${i.product.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t pt-4 space-y-3">
            <div className="flex justify-between font-semibold"><span>Subtotal</span><span data-testid="cart-subtotal">{peso(subtotal)}</span></div>
            <p className="text-xs text-muted-foreground">Delivery fee is calculated at checkout.</p>
            <Button disabled={items.length === 0} className="w-full bg-[#C62828] hover:bg-[#B71C1C] h-12 text-base font-semibold"
              data-testid="cart-checkout-btn" onClick={() => { setOpen(false); nav(user ? "/checkout" : "/login"); }}>
              Proceed to Checkout · {peso(subtotal)}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
